import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useWebSocket, { ReadyState } from "react-use-websocket";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setAdditionalAction,
  setCustomMessage,
  setLabel,
  setOpen,
  setPendingCustomMessage
} from "components/action-snackbar/action-snackbar-slice";
import {
  selectAuthScheme,
  selectCurrentClinic,
  selectCurrentUser,
  selectCurrentUserToken,
  updateCurrentUser
} from "features/auth/auth-slice";
import { wantsCallAlert } from "features/calls/dialstack/call-alerts.util";
import { inboundCallStatusReceived } from "features/calls/dialstack/device-call.slice";
import {
  popBackfilled,
  popReceived
} from "features/calls/dialstack/incoming-call-pop.slice";
import IncomingCallSnackbarAction from "features/calls/dialstack/incoming-call-snackbar-action.component";
import { missedCallReceived } from "features/calls/dialstack/missed-calls.slice";
import { progressReceived } from "features/calls/dialstack/outbound-call-progress.slice";
import {
  loggedInSnackbar,
  loginEndedSnackbar,
  phoneBackSnackbar,
  phoneTakenSnackbar
} from "features/calls/dialstack/softphone/desk-phone/desk-phone-copy";
import { useDialstackSoftphone } from "features/calls/dialstack/softphone/use-dialstack-softphone.hook";
import {
  AMBIENT_SCRIBE_REFETCH,
  eventBus
} from "hooks/use-event-bus/event-bus";
import {
  dispatchTabSyncMessage,
  syncChannel
} from "hooks/use-sync-between-tabs.hook";
import useVoiceSeat from "hooks/use-voice-seat.hook";
import { ambientScribeApi } from "services/ambient-scribe";
import { assignedTagsApi } from "services/assigned-tags";
import { chartNotesApi } from "services/chart-notes";
import { dialstackApi } from "services/dialstack";
import {
  notificationsApi,
  useGetNotificationsQuery,
  useGetUnreadCountsQuery,
  useReadAllNotificationsMutation,
  useUpdateNotificationsMutation
} from "services/notifications";
import { scribeInsightsApi } from "services/scribe-insights";
import { AMBIENT_SCRIBE_NOTE_PARENT_TYPE } from "services/types/ambient-scribe-note.type";
import {
  Notification,
  NotificationFilters,
  NotificationSocketResponse,
  NotificationTypes
} from "services/types/notifications";
import { getDynamicWsUrl } from "utils/api-config.util";
import { buildWebsocketAuthToken } from "utils/auth-header.util";

import {
  AmbientScribeNoteNotificationType,
  AmbientScribeSoapCompletionNotificationType,
  AmbientScribeSummaryCompletionNotificationType,
  BulkAssignTagsCompletedNotificationType
} from "./use-clinic-notifications";

type UnreadCounts = {
  [key: string]: number;
  all: number;
};

const INITIAL_COUNTS: UnreadCounts = {
  all: 0,
  [NotificationTypes.REPORT]: 0,
  [NotificationTypes.MESSAGE]: 0,
  [NotificationTypes.APPOINTMENT]: 0,
  [NotificationTypes.INTAKE_FORM]: 0,
  [NotificationTypes.TASK]: 0,
  [NotificationTypes.MENTION]: 0
};

const NOTIFICATIONS_PAGE_SIZE = 50;

// Screen pop sent by the backend's DialStack webhook (ChannelsMessageType.CALL_INCOMING).
export const DialstackCallIncomingType = "call.incoming";

// An inbound call just ended with nobody on it (ChannelsMessageType.CALL_MISSED, ENG-2476).
// Sent to every voice-enabled staffer of the practice; the missed-call banner and the
// Calls workspace's live row both read it from the missed-calls slice.
export const DialstackCallMissedType = "call.missed";

// The user's Suno calendar started or stopped marking them busy (ENG-2022). Sent to
// the user's own socket only, so the softphone's status dot follows without a reload.
export const VoiceCalendarBusyType = "voice.calendar_busy";

// Progress of a click-to-call the user is taking on their desk phone (ENG-2183).
// Sent to the placing user's own socket only; the softphone widget narrates it.
export const VoiceOutboundCallStatusType = "voice.outbound_call_status";
// An inbound call this user took, and how it is going (ENG-2562): sent only
// to the answerer, so a browser whose ringing leg ended can tell a call taken
// on the user's handset (keep the pop open) from one a colleague took.
export const VoiceInboundCallStatusType = "voice.inbound_call_status";
// A desk phone the user is logged in on, or owns, changed hands or came back
// (ENG-2293): they logged in (from Suno, the phone keypad or the portal), their
// login ended, someone logged in on their own phone, or it returned to them.
// Sent only to the user it concerns.
export const VoiceDeskPhoneStatusType = "voice.desk_phone_status";

// A notification row was written for this user. Exported for the same reason as the
// type above: other components correlate against it rather than re-typing the wire
// string (see notifications-menu/use-hidden-sales-review-count.hook.ts).
export const NewUserNotificationType = "new.user.notification";

// Posted to the other open tabs after this tab marks notifications read, so their
// bell badge and list catch up instead of showing the old unread count until the
// next new notification arrives (Intercom 215476321902574). Marking read sends
// nothing over the websocket, so without this a second tab never finds out.
export const NotificationsReadSyncAction = "notifications.read";

// Longer than the default 4.5s so staff have time to click a View-patient
// button while the phone is still ringing.
const INCOMING_CALL_SNACKBAR_DURATION_MS = 15_000;

const announceNotificationsRead = () => {
  try {
    dispatchTabSyncMessage({ action: NotificationsReadSyncAction });
  } catch {
    // Only a closed channel throws; the other tabs then catch up on focus.
  }
};

type UseNotificationsOptions = {
  // Only the bell passes this. The hook has several instances, and one
  // store-level invalidation per message is enough for all of them.
  syncReadsFromOtherTabs?: boolean;
};

const useNotifications = ({
  syncReadsFromOtherTabs = false
}: UseNotificationsOptions = {}) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  // Ring pops (and their "Incoming call from …" snackbar) are for people with
  // a phone to answer on; the backend sends them only to seat holders too
  // (ENG-2315).
  const hasVoiceSeat = useVoiceSeat();
  const { suppressIncomingSnackbar, setDialDevice } = useDialstackSoftphone();
  const token = useAppSelector(selectCurrentUserToken);
  const authScheme = useAppSelector(selectAuthScheme);
  const user = useAppSelector(selectCurrentUser);
  // Read through a ref: the websocket effect below keys on the last frame, and
  // re-running it for a profile change would replay that frame's snackbar.
  // Synced in an effect declared before that one, so it runs first in a commit.
  const callAlertUser = useRef(user);
  useEffect(() => {
    callAlertUser.current = user;
  }, [user]);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const isSnackbarOpen = useAppSelector((state) =>
    Boolean(state.snackbar?.open)
  );
  const [socketUrl, setSocketUrl] = useState("");
  const [shouldFetchNotifications, setShouldFetchNotifications] =
    useState(false);

  const { sendJsonMessage, lastJsonMessage, readyState, getWebSocket } =
    useWebSocket<NotificationSocketResponse | any>(
      socketUrl,
      { shouldReconnect: () => true },
      Boolean(socketUrl)
    );

  useEffect(() => {
    if (currentClinic?.id && user?.id && user.id !== -1) {
      setSocketUrl(
        `${getDynamicWsUrl()}/user-notifications/${user.id}/clinic/${
          currentClinic?.id
        }/`
      );
    }
  }, [user?.id, currentClinic?.id]);

  useEffect(() => {
    const ws = getWebSocket();
    if (
      readyState === ReadyState.OPEN &&
      token &&
      socketUrl &&
      ws?.readyState === WebSocket.OPEN
    ) {
      try {
        sendJsonMessage({
          method: "authenticate",
          token: buildWebsocketAuthToken(token, authScheme)
        });
      } catch (error) {
        console.error("[useNotifications] Failed to send auth message:", error);
      }
    }
  }, [readyState, sendJsonMessage, token, authScheme, socketUrl, getWebSocket]);

  const [activeType, _setActiveType] = useState<
    NotificationFilters["type"] | null
  >(null);

  const setActiveType = useCallback(
    (type: NotificationFilters["type"] | null) => {
      _setActiveType(type);
      setOffset(0);
      setAccumulatedNotifications([]);
      setHasMore(true);
    },
    []
  );

  // Delay notifications fetch on patients page to prioritize patients query
  useEffect(() => {
    const isPatientsPage =
      location.pathname === "/" || location.pathname.startsWith("/scheduling");
    const shouldDelay = isPatientsPage;

    if (
      !shouldDelay &&
      !shouldFetchNotifications &&
      currentClinic?.id &&
      user?.id
    ) {
      setShouldFetchNotifications(true);
    } else if (
      shouldDelay &&
      !shouldFetchNotifications &&
      currentClinic?.id &&
      user?.id
    ) {
      // Delay fetching notifications on patients page by 2 seconds
      const timer = setTimeout(() => {
        setShouldFetchNotifications(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [
    location.pathname,
    currentClinic?.id,
    user?.id,
    shouldFetchNotifications
  ]);

  const [offset, setOffset] = useState(0);
  const [accumulatedNotifications, setAccumulatedNotifications] = useState<
    Notification[]
  >([]);
  const [hasMore, setHasMore] = useState(true);

  const skipQuery =
    !currentClinic?.id ||
    !user?.id ||
    user.id === -1 ||
    !shouldFetchNotifications;

  // No `clinic` filter: the bell list and unread badge span every clinic the
  // provider belongs to, so a patient-arrived (or other) notification for one of
  // the provider's own appointments shows regardless of the location selected at
  // the top of the app (ENG-1269). Notifications are already user-scoped by
  // `provider`, so dropping the clinic filter does not widen access.
  const { data, refetch, isLoading, isFetching, isUninitialized } =
    useGetNotificationsQuery(
      {
        provider: user.id,
        limit: NOTIFICATIONS_PAGE_SIZE,
        offset,
        ...(activeType != null && { type: activeType })
      },
      { skip: skipQuery }
    );

  // A tab the user comes back to re-reads its counts, so a badge left behind by a
  // read in another tab (or on another computer) corrects itself. Only the counts:
  // the badge is all that is on screen with the bell closed, and the list is
  // re-read when the bell opens, so re-reading 50 rows on every alt-tab buys
  // nothing.
  const { data: unreadCountsData, refetch: refetchUnreadCounts } =
    useGetUnreadCountsQuery(
      { provider: user.id },
      { skip: skipQuery, refetchOnFocus: true }
    );

  useEffect(() => {
    if (!data) return;
    if (offset === 0) {
      setAccumulatedNotifications(data);
    } else {
      setAccumulatedNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        const newItems = data.filter((n) => !existingIds.has(n.id));
        return [...prev, ...newItems];
      });
    }
    setHasMore(data.length >= NOTIFICATIONS_PAGE_SIZE);
  }, [data, offset]);

  useEffect(() => {
    if (
      lastJsonMessage?.type === NewUserNotificationType ||
      lastJsonMessage?.type === "stafftaskcomment.create.notification"
    ) {
      if (!isLoading && !isUninitialized) {
        setOffset(0);
        refetch();
        refetchUnreadCounts();
      }
      // A Sales Coach review (`extra.review_id`, SN-19629) also arrives as type 8,
      // but it writes nothing to a chart note or to the scribe, so none of this
      // applies. Excluded rather than left as harmless noise: the root CLAUDE.md
      // records that a gratuitous ChartNotes invalidation risks the `usePagination`
      // page-0 wipe (SN-18212 — pinned chart notes vanishing on refresh).
      if (
        lastJsonMessage?.message?.type === NotificationTypes.AMBIENT_SCRIBE &&
        lastJsonMessage?.message?.extra?.review_id == null
      ) {
        dispatch(chartNotesApi.util.invalidateTags(["ChartNotes"]));
        eventBus.emit(AMBIENT_SCRIBE_REFETCH, undefined);
        // "Insights generated" notifications carry extra.insight_count; reload the
        // scribe-insights feed/count/table so an open page refreshes without a reload.
        if (lastJsonMessage?.message?.extra?.insight_count != null) {
          dispatch(scribeInsightsApi.util.invalidateTags(["ScribeInsights"]));
        }
      }
    } else if (
      lastJsonMessage?.type === AmbientScribeSoapCompletionNotificationType ||
      lastJsonMessage?.type === AmbientScribeSummaryCompletionNotificationType
    ) {
      dispatch(ambientScribeApi.util.invalidateTags(["AmbientScribes"]));
      dispatch(chartNotesApi.util.invalidateTags(["ChartNotes"]));
      eventBus.emit(AMBIENT_SCRIBE_REFETCH, undefined);
    } else if (lastJsonMessage?.type === AmbientScribeNoteNotificationType) {
      // A generated note finished (SN-19067) — succeeded or failed. This is a latency
      // optimisation, not the completion signal: the audiology UI already detects
      // `is_generating_audiology_summary` flipping to false on its own 2.5s poll, so the flow
      // works with this message dropped. Arriving here just saves that wait.
      const scribeId = lastJsonMessage?.message?.scribe_id;
      dispatch(
        ambientScribeApi.util.invalidateTags([
          "AmbientScribeNotes",
          // Targeted when the payload names the scribe, unlike the SOAP branch above which has
          // no id to work with and has to refetch every cached scribe query. This only reaches
          // list reads because `provideAmbientScribeListTags` gives them per-row ids — an
          // id-scoped invalidation does NOT match a plain-tag provider.
          scribeId != null
            ? { type: "AmbientScribes" as const, id: Number(scribeId) }
            : "AmbientScribes"
        ])
      );
      // A `ChartNote`-parented note DID rewrite a chart note, so its text has to be refetched.
      // Still skipped for the audiology parent type, where nothing writes a chart note and a
      // gratuitous invalidation risks the `usePagination` page-0 wipe (SN-18212).
      if (
        lastJsonMessage?.message?.parent_type ===
        AMBIENT_SCRIBE_NOTE_PARENT_TYPE.CHART_NOTE
      ) {
        dispatch(chartNotesApi.util.invalidateTags(["ChartNotes"]));
      }
    } else if (
      lastJsonMessage?.type === BulkAssignTagsCompletedNotificationType
    ) {
      dispatch(
        assignedTagsApi.util.invalidateTags(["AssignedPatientTags", "Patients"])
      );
      const patientsCount = lastJsonMessage?.message?.patients_count;
      if (patientsCount != null) {
        dispatch(
          setCustomMessage(`Tags assigned to ${patientsCount} patients`)
        );
        dispatch(setOpen(true));
      }
    } else if (
      lastJsonMessage?.type === DialstackCallIncomingType &&
      hasVoiceSeat
    ) {
      const pop = lastJsonMessage?.message;
      const patientIds: number[] = Array.isArray(pop?.patient_ids)
        ? pop.patient_ids
        : [];
      // Always stored: the softphone widget's patient-match card joins this
      // pop to the SDK's ringing call by phone number.
      if (pop?.call_id) {
        dispatch(
          popReceived({
            callId: String(pop.call_id),
            backendCallId: typeof pop.id === "number" ? pop.id : null,
            clinicId: pop.clinic_id ?? null,
            fromNumber: pop.from_number ?? "",
            fromName: pop.from_name ?? null,
            matchedOn: pop.matched_on ?? null,
            patientIds,
            contact: pop.contact ?? null,
            transfer: pop.transfer
              ? {
                  byName: pop.transferred_by_name || null,
                  originalFromNumber: pop.original_from_number || null,
                  originalFromName: pop.original_from_name || null
                }
              : null,
            receivedAt: Date.now()
          })
        );
      }
      // The connected softphone shows its own ringing UI for this call; the
      // snackbar remains the fallback for users without a softphone session.
      // A transfer rings one colleague but its pop fans out clinic-wide, so
      // it never announces itself to everyone else. A location the user chose
      // not to hear about (ENG-2513) is stored for the softphone but not
      // announced, and so is every call for someone who turned the pop-up
      // off (ENG-2597).
      if (
        !suppressIncomingSnackbar &&
        !pop?.transfer &&
        callAlertUser.current?.incoming_call_popups_enabled !== false &&
        wantsCallAlert(callAlertUser.current, pop?.clinic_id ?? null)
      ) {
        const caller =
          pop?.contact?.name ||
          pop?.from_name ||
          pop?.from_number ||
          "Unknown caller";
        dispatch(setCustomMessage(`Incoming call from ${caller}`));
        if (patientIds.length > 0) {
          dispatch(
            setAdditionalAction(
              <IncomingCallSnackbarAction patientIds={patientIds} />
            )
          );
        }
        dispatch(setOpen(INCOMING_CALL_SNACKBAR_DURATION_MS));
      }
    } else if (
      lastJsonMessage?.type === DialstackCallMissedType &&
      hasVoiceSeat
    ) {
      const missed = lastJsonMessage?.message;
      if (typeof missed?.id === "number") {
        dispatch(
          missedCallReceived({
            callId: missed.id,
            providerCallId: missed.call_id ?? null,
            clinicId: missed.clinic_id ?? null,
            status: missed.status ?? null,
            fromNumber: missed.from_number ?? "",
            fromName: missed.from_name || null,
            callerLabel:
              missed.caller_label || missed.from_number || "Unknown caller",
            patientIds: Array.isArray(missed.patient_ids)
              ? missed.patient_ids
              : [],
            contact: missed.contact ?? null,
            endedAt: missed.ended_at ?? null,
            receivedAt: Date.now()
          })
        );
        // The workspace's today summary (calls / missed) is a plain query, not
        // the paginated list, so a refetch is safe (SN-18212 is about the list).
        dispatch(dialstackApi.util.invalidateTags(["DialstackCallMetrics"]));
      }
    } else if (lastJsonMessage?.type === VoiceCalendarBusyType) {
      dispatch(
        updateCurrentUser({
          voice_calendar_busy:
            lastJsonMessage?.message?.voice_calendar_busy ?? ""
        })
      );
    } else if (lastJsonMessage?.type === VoiceOutboundCallStatusType) {
      const progress = lastJsonMessage?.message ?? {};
      dispatch(
        progressReceived({
          event: progress.event,
          callId: progress.call_id,
          toNumber: progress.to_number ?? "",
          fromNumber: progress.from_number ?? "",
          patientId: progress.patient_id ?? null,
          callStatus: progress.call_status ?? null,
          answeredAt: progress.answered_at ?? null,
          endedAt: progress.ended_at ?? null,
          durationSeconds: progress.duration_seconds ?? null,
          receivedAt: Date.now()
        })
      );
    } else if (lastJsonMessage?.type === VoiceInboundCallStatusType) {
      const status = lastJsonMessage?.message ?? {};
      if (status.call_id) {
        dispatch(
          inboundCallStatusReceived({
            event: status.event,
            callId: String(status.call_id),
            backendCallId: typeof status.id === "number" ? status.id : null,
            clinicId: status.clinic_id ?? null,
            callStatus: status.call_status ?? null,
            answeredAt: status.answered_at ?? null,
            endedAt: status.ended_at ?? null,
            durationSeconds: status.duration_seconds ?? null,
            receivedAt: Date.now()
          })
        );
        // The frame carries the caller like the ring-time pop (ENG-2605), so
        // a browser that never saw the call ring — opened mid-call — can
        // still build the card. A pop that is already here always wins.
        if (typeof status.from_number === "string") {
          dispatch(
            popBackfilled({
              callId: String(status.call_id),
              backendCallId: typeof status.id === "number" ? status.id : null,
              clinicId: status.clinic_id ?? null,
              fromNumber: status.from_number,
              fromName: status.from_name || null,
              matchedOn: status.matched_on ?? null,
              patientIds: Array.isArray(status.patient_ids)
                ? status.patient_ids
                : [],
              contact: status.contact ?? null,
              transfer: status.transferred_by_name
                ? {
                    byName: status.transferred_by_name,
                    originalFromNumber: null,
                    originalFromName: null
                  }
                : null,
              receivedAt: Date.now()
            })
          );
        }
      }
    } else if (lastJsonMessage?.type === VoiceDeskPhoneStatusType) {
      // Never trusted for state: the lists are refetched. The frame only says
      // what to tell the person — and, for their own login, where their calls
      // now ring, so dials follow a login made at the keypad or in the portal.
      const change = lastJsonMessage?.message ?? {};
      dispatch(
        dialstackApi.util.invalidateTags([
          "DialstackDeskPhones",
          "DialstackCallerIds"
        ])
      );
      const name: string = change.device_name || "your desk phone";
      const fromThisApp = change.via === "suno_app";
      let message: string | null = null;
      if (change.event === "logged_in") {
        setDialDevice("deskphone");
        if (!fromThisApp) message = loggedInSnackbar(name);
      } else if (change.event === "logged_out") {
        setDialDevice("softphone");
        if (!fromThisApp || change.reason === "reset")
          message = loginEndedSnackbar(
            name,
            change.reason ?? "",
            change.by_name ?? ""
          );
      } else if (change.event === "phone_taken")
        message = phoneTakenSnackbar(name, change.by_name || "A colleague");
      else if (change.event === "phone_back") message = phoneBackSnackbar(name);
      if (message) {
        dispatch(setCustomMessage(message));
        dispatch(setOpen(true));
      }
    }
  }, [
    dispatch,
    hasVoiceSeat,
    isLoading,
    isUninitialized,
    lastJsonMessage,
    refetch,
    refetchUnreadCounts,
    setDialDevice,
    suppressIncomingSnackbar
  ]);

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setOffset((prev) => prev + NOTIFICATIONS_PAGE_SIZE);
    }
  }, [isFetching, hasMore]);

  const [updateNotification] = useUpdateNotificationsMutation();

  const readNotification = useCallback(
    (id: number) => {
      updateNotification({ id, is_read: true })
        .unwrap()
        .then(announceNotificationsRead)
        .catch(() => {});
      setAccumulatedNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    },
    [updateNotification]
  );

  const [readAllNotifications, { isLoading: isReadingAll }] =
    useReadAllNotificationsMutation();

  // Resolves to whether the server marked them read, so the bell only closes on
  // success. A 4xx is otherwise swallowed (see the root CLAUDE.md on `.unwrap()`),
  // which left the user staring at the same unread rows with no explanation.
  const handleReadAllNotifications = useCallback(async () => {
    try {
      await readAllNotifications(user.id).unwrap();
    } catch {
      const message =
        "Notifications could not be marked as read. Please try again.";
      // Same as the global error toast in `utils/services.util.ts`: never
      // overwrite an open toast (an incoming call's keeps its action button).
      if (isSnackbarOpen) {
        dispatch(setPendingCustomMessage(message));
      } else {
        dispatch(setLabel(""));
        dispatch(setAdditionalAction(undefined));
        dispatch(setCustomMessage(message));
        dispatch(setOpen(true));
      }
      return false;
    }
    setOffset(0);
    setAccumulatedNotifications([]);
    announceNotificationsRead();
    return true;
  }, [dispatch, isSnackbarOpen, user.id, readAllNotifications]);

  const deleteNotification = useCallback(
    (id: number) => {
      updateNotification({ id, is_active: false, is_read: true })
        .unwrap()
        .then(announceNotificationsRead)
        .catch(() => {});
      setAccumulatedNotifications((prev) => prev.filter((n) => n.id !== id));
    },
    [updateNotification]
  );

  // The list AND the unread counts: opening the bell used to refresh only the
  // list, so a stale badge sat over a list in which every row was read.
  const refetchNotifications = useCallback(() => {
    if (!isLoading && !isUninitialized) {
      setOffset(0);
      refetch();
      refetchUnreadCounts();
    }
  }, [isLoading, isUninitialized, refetch, refetchUnreadCounts]);

  // Read straight off the sync channel rather than through useSyncBetweenTabs:
  // its emitterId self-filter drops messages between tabs that share an
  // appInstanceId, which a duplicated tab or one opened with target="_blank"
  // does (sessionStorage is copied). BroadcastChannel never delivers back to
  // the posting tab, and invalidating twice is harmless, so neither the
  // self-filter nor the messageId dedupe is needed. Same reasoning as the
  // arrival alerts in layout-main-content.component.tsx.
  //
  // Invalidating rather than calling `refetch` drops cached pages nobody is
  // subscribed to (the offset-0 page while the bell is scrolled), defers the
  // refetch past a request already in flight instead of losing it, and is safe
  // before the queries have started.
  useEffect(() => {
    if (!syncReadsFromOtherTabs) return;
    const handleMessage = (event: MessageEvent) => {
      let data: { action?: string };
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      if (data?.action !== NotificationsReadSyncAction) return;
      setOffset(0);
      dispatch(
        notificationsApi.util.invalidateTags([
          "Notifications",
          "NotificationUnreadCounts"
        ])
      );
    };
    syncChannel.addEventListener("message", handleMessage);
    return () => syncChannel.removeEventListener("message", handleMessage);
  }, [dispatch, syncReadsFromOtherTabs]);

  const unreadNotificationsCount = useMemo<UnreadCounts>(() => {
    if (!unreadCountsData) return INITIAL_COUNTS;

    const all = Object.values(unreadCountsData).reduce(
      (sum, count) => sum + count,
      0
    );
    return {
      ...INITIAL_COUNTS,
      ...Object.fromEntries(
        Object.entries(unreadCountsData).map(([type, count]) => [type, count])
      ),
      all
    };
  }, [unreadCountsData]);

  return {
    notifications: accumulatedNotifications,
    readNotification,
    deleteNotification,
    setActiveType,
    activeType,
    unreadNotificationsCount,
    handleReadAllNotifications,
    isReadingAll,
    refetch: refetchNotifications,
    loadMore,
    hasMore,
    isLoadingMore: isFetching && offset > 0,
    // Exposed so components can watch for a specific message type/id (e.g. the
    // catalog CSV import completion event, SN-19052) without adding a case to
    // the switch above — that switch only drives global side effects
    // (snackbar, tag invalidation); per-component correlation belongs here.
    lastJsonMessage
  };
};

export default useNotifications;
